import { useRef } from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import { Hyperlink, TableCol, Text, TablePagination, Spacing, Table } from 'zens-storybook'
import { ProfileStatus, User, useUsers } from 'zens-store'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'zens-router'

import { titleForRole } from '@dms/helper'

import { TablePaginated } from '../table'

import UserEmpty from './empty'
import UserCell from './cell'
import UserStatusBadge from './status'

const styles = StyleSheet.create({
  status: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  resend: {
    fontSize: 12,
    marginLeft: Spacing.S,
  },
})

interface Props extends ViewProps {
  companyId: number
  search?: string
  pagination?: TablePagination
  onPaginationChange?: (pagination: TablePagination) => void
  onResendInvite?: (user: User) => void
}

const UserTable = (props: Props) => {
  const { companyId, search, pagination, onPaginationChange, onResendInvite, ...otherProps } = props
  const { t } = useTranslation()
  const router = useRouter()
  const users = useUsers({ companyId, search })
  const skipNavigation = useRef(false)

  const rows =
    users.data?.map((user) => ({
      data: user,
      onPress: () => {
        if (skipNavigation.current) {
          skipNavigation.current = false
          return
        }
        router.push(`/companies/${companyId}/users/${user.id}`)
      },
      accessory: false as const,
    })) || []

  const cols: TableCol<User>[] = [
    {
      key: 'displayName',
      title: t('Name'),
      render: (user) => <UserCell user={user} />,
    },
    {
      key: 'roleId',
      title: t('Role'),
      render: (user) => <Text>{titleForRole(user.roleId, t)}</Text>,
      size: {
        width: 200,
      },
    },
    // {
    //   key: 'location',
    //   title: t('Location access'),
    //   render: () => <Text>{t('All')}</Text>,
    //   size: {
    //     width: 200,
    //   },
    // },
    {
      key: 'status',
      title: t('Status'),
      render: (user) => (
        <View style={styles.status}>
          <UserStatusBadge status={user.status} />
          {user.status === ProfileStatus.Invited && (
            <Hyperlink
              title={t('Resend invite')}
              textStyle={styles.resend}
              onPress={() => {
                skipNavigation.current = true
                onResendInvite?.(user)
              }}
              testID="UserList.ResendInvite"
            />
          )}
        </View>
      ),
      size: {
        width: 200,
      },
    },
  ]

  return (
    <TablePaginated
      rows={rows}
      cols={cols}
      count={rows.length}
      renderCount={() =>
        t('{{page}} of {{numberOfPages}}', {
          page: (pagination?.page || 0) + 1,
          numberOfPages: Math.ceil(rows.length / (pagination?.size || 10)),
        })
      }
      sizeLabel={t('Users per page')}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      bottom={`${t('Active users')}: ${
        users.data?.filter((user) => user.status === ProfileStatus.Active).length || 0
      }/${users.data?.length}`}
      ListEmptyComponent={<UserEmpty companyId={companyId} search={search} />}
      {...otherProps}
    />
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()

  const cols: TableCol<User>[] = [
    {
      key: 'displayName',
      title: t('Name'),
    },
    {
      key: 'roleId',
      title: t('Role'),
      size: {
        width: 200,
      },
    },
    // {
    //   key: 'location',
    //   title: t('Location access'),
    //   size: {
    //     width: 200,
    //   },
    // },
    {
      key: 'status',
      title: t('Status'),
      size: {
        width: 200,
      },
    },
  ]

  return <Table.Skeleton cols={cols} hasAccessory {...props} />
}
UserTable.Skeleton = Skeleton

export default UserTable
